import {Module} from '@fantasmagames/slot/src/common/Module';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {BonusGameEvents} from '@fantasmagames/slot/src/modules/bonus/BonusGameEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FlowAvalancheState} from '@fantasmagames/slot/src/modules/flow/states/FlowAvalancheState';
import {FlowBasicState} from '@fantasmagames/slot/src/modules/flow/states/FlowBasicState';
import {FlowBonusState} from '@fantasmagames/slot/src/modules/flow/states/FlowBonusState';
import {FlowFreeSpinsState} from '@fantasmagames/slot/src/modules/flow/states/FlowFreeSpinsState';
import {FlowRespinState} from '@fantasmagames/slot/src/modules/flow/states/FlowRespinState';
import {FlowRestoreState} from '@fantasmagames/slot/src/modules/flow/states/FlowRestoreState';
import {FlowWhileSpinningState} from '@fantasmagames/slot/src/modules/flow/states/FlowWhileSpinningState';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {AvalancheReelsEvents} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsEvents';
import {IndividualSymbolReelsEvents} from '@fantasmagames/slot/src/modules/reels/individual/IndividualSymbolReelsEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {RestoreScreenEvents} from '@fantasmagames/slot/src/modules/restoreScreen/RestoreScreenEvents';

export class FlowModule extends Module<null> {
	public static readonly RESTORE_STATE: string = 'restore';
	public static readonly BASIC_STATE: string = 'basic';
	public static readonly FREE_SPINS_STATE: string = 'freespins';
	public static readonly BONUS_STATE: string = 'bonus';
	public static readonly RESPIN_STATE: string = 'respin';
	public static readonly AVALANCHE_STATE: string = 'avalanche';
	public static readonly WHILESPIN_STATE: string = 'whilespin';

	protected _isRestore: boolean = false;
	protected _isInsideFreeSpins: boolean = false;
	protected _isInsideFreeSpinsRestore: boolean = false;

	private _isRestoreMessageOnTheScreen: boolean = false;

	protected setupStates(): void {
		this.addState(new FlowBasicState(this, FlowModule.BASIC_STATE, this.eventManager));
		this.addState(new FlowFreeSpinsState(this, FlowModule.FREE_SPINS_STATE, this.eventManager));
		this.addState(new FlowBonusState(this, FlowModule.BONUS_STATE, this.eventManager));
		this.addState(new FlowRestoreState(this, FlowModule.RESTORE_STATE));
		this.addState(new FlowRespinState(this, FlowModule.RESPIN_STATE, this.eventManager));
		this.addState(new FlowAvalancheState(this, FlowModule.AVALANCHE_STATE, this.eventManager));
		this.addState(new FlowWhileSpinningState(this, FlowModule.WHILESPIN_STATE, this.eventManager));
	}

	public restore(): void {
		super.restore();

		if (this.logic.isFreeSpinsStarted) {
			this._isInsideFreeSpins = true;
			this.gotoState(FlowModule.FREE_SPINS_STATE);
		}
	}

	public afterInit(): void {
		this.eventManager.emit(FlowModuleEvents.CAN_SPIN);
		this.system.endLoading();
	}

	protected setupEvents(): void {
		this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);

		this.onCurrentStateHandle(AvalancheReelsEvents.AVALANCHE_FINISHED);
		this.onCurrentStateHandle(AvalancheReelsEvents.SYMBOLS_RESPIN_FINISHED);
		this.onCurrentStateHandle(AvalancheReelsEvents.SYMBOLS_RESPIN_STARTED);
		this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);

		this.onCurrentStateHandle(LogicEvents.PICK_RESPONSE);
		this.onCurrentStateHandle(LogicEvents.SKIPPICK_RESPONSE);
		this.onCurrentStateHandle(LogicEvents.SPIN_RESPONSE);
		this.onCurrentStateHandle(FlowModuleEvents.CAN_CONTINUE_SPIN);

		this.onCurrentStateHandle(BonusGameEvents.BONUS_OUTRO_FINISHED);
		this.onCurrentStateHandle(AvalancheReelsEvents.ADDING_NEW_FINISHED);
		this.onCurrentStateHandle(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED);
		this.onCurrentStateHandle(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED);
		this.onCurrentStateHandle(IndividualSymbolReelsEvents.INDIVIDUAL_RESPIN_FINISHED);

		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted);
		this.on(ReelsEvents.REELS_RESPIN_STARTED, this.onReelsReSpinningStarted);

		this.on(RestoreScreenEvents.RESTORE_WINDOW_CLOSED, this.onRestoreWindowClosed);
		this.on(RestoreScreenEvents.RESTORE_WINDOW_OPENED, this.onRestoreWindowOpened);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_RESTORE_INTRO_STARTED, this.onFreeSpinsRestoreIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED, this.onFreeSpinsIntroFinished);
	}

	protected onFreeSpinsIntroStarted(): void {
		this._isInsideFreeSpins = true;
	}

	protected onFreeSpinsRestoreIntroStarted(): void {
		this._isInsideFreeSpinsRestore = true;
	}

	protected onFreeSpinsIntroFinished(): void {
		if (this._isInsideFreeSpinsRestore) {
			this.gotoState(FlowModule.FREE_SPINS_STATE);
			this._isInsideFreeSpinsRestore = false;
		}
	}

	protected onFreeSpinsOutroFinished(): void {
		this._isInsideFreeSpins = false;
	}

	protected onRestoreWindowOpened(): void {
		this._isRestoreMessageOnTheScreen = true;
	}

	protected onRestoreWindowClosed(): void {
		this._isRestoreMessageOnTheScreen = false;
		if (!this._isInsideFreeSpinsRestore) {
			this._currentState.handle(ReelsEvents.SPIN_ENDED);
		}
	}

	protected onReelsSpinningStarted(): void {
		if (!this.logic.isFreeSpinsStarted) {
			this.gotoState(FlowModule.BASIC_STATE);
		}
		this._currentState.handle(ReelsEvents.REELS_SPINNING_STARTED);

		this.gotoState(FlowModule.WHILESPIN_STATE);
	}

	protected onReelsReSpinningStarted(): void {}

	protected setupViews(): void {}

	protected enterFirstState(): void {
		this.changeStateTo(FlowModule.BASIC_STATE);
	}

	public get isRestore(): boolean {
		return this._isRestore;
	}

	public set isRestore(isRestore: boolean) {
		this._isRestore = isRestore;
	}

	public get isInsideFreeSpins(): boolean {
		return this._isInsideFreeSpins;
	}

	public get isInsideFreeSpinsRestore(): boolean {
		return this._isInsideFreeSpinsRestore;
	}

	public get isRestoreMessageOnTheScreen(): boolean {
		return this._isRestoreMessageOnTheScreen;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/flow/FlowModule.ts